import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Shield } from 'lucide-react';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role !== 'admin') {
        // Logout the user if not admin
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }
      toast.success(`Welcome back, ${user.first_name}!`);
      navigate('/admin');
    } catch (error) {
      toast.error(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="page-banner">
        <div className="container-wide">
          <h1>ADMIN LOGIN</h1>
          <div className="breadcrumb">
            <Link to="/">Home</Link> &gt; Admin Login
          </div>
        </div>
      </div>

      <section className="content-section">
        <div className="container-narrow" style={{maxWidth: '400px'}}>
          {/* Login Type Toggle */}
          <div className="flex gap-2 mb-8">
            <Link
              to="/login"
              className="flex-1 py-3 px-4 bg-[#2d4a6f] text-white font-semibold text-sm uppercase tracking-wide text-center hover:bg-[#1a365d] transition-colors"
              data-testid="client-login-tab"
            >
              Client Login
            </Link>
            <button
              className="flex-1 py-3 px-4 bg-[#1a365d] text-white font-semibold text-sm uppercase tracking-wide"
              data-testid="admin-login-tab"
            >
              Admin Login
            </button>
          </div>

          <div className="text-center mb-8">
            <Shield className="h-12 w-12 mx-auto text-[#1a365d] mb-3" />
            <p className="text-gray-600">
              Admin portal access. Enter your administrator credentials.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label className="form-label">Admin Email</label>
              <Input
                type="email"
                placeholder="admin@archoninsurance.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input pr-10"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="btn-navy w-full flex items-center justify-center"
              disabled={loading}
              data-testid="admin-login-submit-btn"
            >
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : 'Admin Sign In'}
            </button>
          </form>

          <hr className="content-divider" />

          <p className="text-center text-gray-500 text-sm">
            Need admin access? Contact your system administrator.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminLoginPage;
